import React, { useState } from 'react';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';
import StudentVerification from './StudentVerification';
import RoomSelection from './RoomSelection';
import PaymentIntegration from './PaymentIntegration';
import BookingConfirmation from './BookingConfirmation';

const HostelBookingForm = ({ hostel, onBack }) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [bookingData, setBookingData] = useState({
    studentInfo: {},
    roomSelection: {},
    paymentInfo: {},
    bookingDetails: {
      checkInDate: '',
      duration: 'semester', // semester, academic_year, custom
      specialRequests: '',
      emergencyContact: {
        name: '',
        phone: '',
        relationship: ''
      }
    }
  });

  const steps = [
    { id: 1, title: 'Student Verification', description: 'Verify your university enrollment' },
    { id: 2, title: 'Room Selection', description: 'Choose your preferred room type' },
    { id: 3, title: 'Booking Details', description: 'Set check-in date and duration' },
    { id: 4, title: 'Payment', description: 'Complete your booking payment' },
    { id: 5, title: 'Confirmation', description: 'Review and confirm booking' }
  ];

  const updateBookingData = (step, data) => {
    setBookingData(prev => ({
      ...prev,
      [step]: data
    }));
  };

  const nextStep = () => {
    if (currentStep < steps.length) {
      setCurrentStep(currentStep + 1);
    }
  };

  const prevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <StudentVerification
            data={bookingData.studentInfo}
            onUpdate={(data) => updateBookingData('studentInfo', data)}
            onNext={nextStep}
          />
        );
      case 2:
        return (
          <RoomSelection
            hostel={hostel}
            data={bookingData.roomSelection}
            onUpdate={(data) => updateBookingData('roomSelection', data)}
            onNext={nextStep}
            onPrev={prevStep}
          />
        );
      case 3:
        return (
          <BookingDetailsForm
            data={bookingData.bookingDetails}
            onUpdate={(data) => updateBookingData('bookingDetails', data)}
            onNext={nextStep}
            onPrev={prevStep}
          />
        );
      case 4:
        return (
          <PaymentIntegration
            bookingData={bookingData}
            hostel={hostel}
            onUpdate={(data) => updateBookingData('paymentInfo', data)}
            onNext={nextStep}
            onPrev={prevStep}
          />
        );
      case 5:
        return (
          <BookingConfirmation
            bookingData={bookingData}
            hostel={hostel}
            onConfirm={() => {
              // Handle final booking confirmation
              alert('Booking confirmed! You will receive a confirmation email shortly.');
              onBack();
            }}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="bg-card border-b border-border px-4 py-4">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <div className="flex items-center">
            <Button variant="ghost" size="sm" onClick={onBack}>
              <Icon name="ArrowLeft" size={16} className="mr-2" />
              Back to Hostel
            </Button>
          </div>
          <div className="text-center">
            <h1 className="text-xl font-semibold">Book {hostel.name}</h1>
            <p className="text-sm text-muted-foreground">{hostel.university}</p>
          </div>
          <div className="w-20"></div> {/* Spacer for centering */}
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Progress Steps */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            {steps.map((step, index) => (
              <div key={step.id} className="flex items-center">
                <div className={`flex items-center justify-center w-8 h-8 rounded-full text-sm font-medium ${
                  step.id <= currentStep
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-muted text-muted-foreground'
                }`}>
                  {step.id < currentStep ? (
                    <Icon name="Check" size={16} />
                  ) : (
                    step.id
                  )}
                </div>
                <div className="ml-3 hidden sm:block">
                  <p className={`text-sm font-medium ${
                    step.id <= currentStep ? 'text-foreground' : 'text-muted-foreground'
                  }`}>
                    {step.title}
                  </p>
                  <p className="text-xs text-muted-foreground">{step.description}</p>
                </div>
                {index < steps.length - 1 && (
                  <div className={`w-12 h-0.5 mx-4 ${
                    step.id < currentStep ? 'bg-primary' : 'bg-muted'
                  }`} />
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Step Content */}
        <div className="bg-card rounded-lg border border-border p-6">
          {renderStepContent()}
        </div>
      </div>
    </div>
  );
};

// Booking Details Form Component
const BookingDetailsForm = ({ data, onUpdate, onNext, onPrev }) => {
  const [formData, setFormData] = useState(data);

  const handleSubmit = (e) => {
    e.preventDefault();
    onUpdate(formData);
    onNext();
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleEmergencyContactChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      emergencyContact: {
        ...prev.emergencyContact,
        [field]: value
      }
    }));
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div>
        <h2 className="text-xl font-semibold mb-4">Booking Details</h2>
        <p className="text-muted-foreground mb-6">
          Please provide your preferred check-in date and booking duration.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Check-in Date */}
        <div>
          <label className="block text-sm font-medium text-foreground mb-2">
            Preferred Check-in Date *
          </label>
          <input
            type="date"
            value={formData.checkInDate}
            onChange={(e) => handleInputChange('checkInDate', e.target.value)}
            min={new Date().toISOString().split('T')[0]}
            className="w-full px-3 py-2 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
            required
          />
          <p className="text-xs text-muted-foreground mt-1">
            Check-in typically starts at 2:00 PM
          </p>
        </div>

        {/* Booking Duration */}
        <div>
          <label className="block text-sm font-medium text-foreground mb-2">
            Booking Duration *
          </label>
          <select
            value={formData.duration}
            onChange={(e) => handleInputChange('duration', e.target.value)}
            className="w-full px-3 py-2 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
            required
          >
            <option value="semester">One Semester (4-5 months)</option>
            <option value="academic_year">Academic Year (9-10 months)</option>
            <option value="custom">Custom Duration</option>
          </select>
        </div>
      </div>

      {/* Special Requests */}
      <div>
        <label className="block text-sm font-medium text-foreground mb-2">
          Special Requests or Medical Conditions
        </label>
        <textarea
          value={formData.specialRequests}
          onChange={(e) => handleInputChange('specialRequests', e.target.value)}
          rows={3}
          className="w-full px-3 py-2 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent resize-none"
          placeholder="Any special accommodation needs, medical conditions, or preferences..."
        />
      </div>

      {/* Emergency Contact */}
      <div>
        <h3 className="text-lg font-medium mb-4">Emergency Contact Information</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Full Name *
            </label>
            <input
              type="text"
              value={formData.emergencyContact.name}
              onChange={(e) => handleEmergencyContactChange('name', e.target.value)}
              className="w-full px-3 py-2 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
              placeholder="Parent/Guardian name"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Phone Number *
            </label>
            <input
              type="tel"
              value={formData.emergencyContact.phone}
              onChange={(e) => handleEmergencyContactChange('phone', e.target.value)}
              className="w-full px-3 py-2 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
              placeholder="+256 XXX XXX XXX"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Relationship *
            </label>
            <select
              value={formData.emergencyContact.relationship}
              onChange={(e) => handleEmergencyContactChange('relationship', e.target.value)}
              className="w-full px-3 py-2 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
              required
            >
              <option value="">Select relationship</option>
              <option value="parent">Parent</option>
              <option value="guardian">Guardian</option>
              <option value="sibling">Sibling</option>
              <option value="relative">Relative</option>
              <option value="friend">Friend</option>
              <option value="other">Other</option>
            </select>
          </div>
        </div>
      </div>

      {/* Terms and Conditions */}
      <div className="bg-muted/50 p-4 rounded-lg">
        <div className="flex items-start">
          <input
            type="checkbox"
            id="terms"
            className="mt-1 mr-3"
            required
          />
          <div>
            <label htmlFor="terms" className="text-sm font-medium cursor-pointer">
              I agree to the hostel rules and regulations *
            </label>
            <p className="text-xs text-muted-foreground mt-1">
              By booking, you agree to follow all hostel rules, including quiet hours, visitor policies, and academic requirements.
            </p>
          </div>
        </div>
      </div>

      {/* Navigation */}
      <div className="flex justify-between pt-6">
        <Button type="button" variant="outline" onClick={onPrev}>
          <Icon name="ArrowLeft" size={16} className="mr-2" />
          Previous
        </Button>
        <Button type="submit">
          Continue to Payment
          <Icon name="ArrowRight" size={16} className="ml-2" />
        </Button>
      </div>
    </form>
  );
};

export default HostelBookingForm;